import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import type { SelectProps as MuiSelectProps } from '@mui/material/Select'
import { alpha, useTheme } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'

import LabelWithTooltip from '@/components/LabelWithTooltip'
import { ITEM_HEIGHT, ITEM_PADDING_TOP } from '@/utils/constants'

export interface SelectOption {
  optishade_supported: boolean
  camera_supported: boolean
  internal_effects_activated: any
  value: string | number
  text: string
  disabled?: boolean
}

export interface CustomSelectProps
  extends Omit<MuiSelectProps, 'value' | 'onChange'> {
  name: string
  control?: Control<any>
  title?: string
  tooltipText?: string
  titleSize?: 'small' | 'medium' | 'large'
  options: SelectOption[]
  error?: boolean
  required?: boolean
  fullWidth?: boolean
  width?: string
  placeholder?: string
  menuStyle?: any
  onFormValueChange?: (name: string, value: any) => void
  showError?: boolean
  direction?: 'column' | 'row'
  defaultValue?: any
  outlinedLabel?: boolean
  titleSx?: SxProps<Theme>
  optionDisabledChecker?: (item: SelectOption) => boolean
  inputLabel?: any
}

const Select = ({
  name = '',
  control,
  title,
  tooltipText,
  titleSize = 'large',
  options = [],
  error = false,
  required = false,
  fullWidth = false,
  size = 'medium',
  disabled = false,
  width = '100%',
  sx,
  placeholder = 'Select',
  menuStyle,
  onFormValueChange,
  showError = true,
  direction = 'column',
  defaultValue = '',
  outlinedLabel = false,
  titleSx = {},
  optionDisabledChecker,
  inputLabel,
  ...rest
}: CustomSelectProps) => {
  const theme = useTheme()
  const [value, setValue] = useState(defaultValue)
  const [fieldError, setFieldError] = useState(false)

  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  const getTitleFontSize = (size: 'small' | 'medium' | 'large') => {
    switch (size) {
      case 'small':
        return '1rem'
      case 'medium':
        return '1.25rem'
      case 'large':
        return '1.125rem'
      default:
        return '1rem'
    }
  }

  const renderTitle = () => {
    if (!title) return null

    if (titleSize === 'large') {
      return (
        <LabelWithTooltip
          label={title}
          tooltipText={tooltipText || title}
          titleSx={{
            fontSize: getTitleFontSize(titleSize),
            fontWeight: 500,
            lineHeight: 'normal',
            ...titleSx,
          }}
          sx={{ whiteSpace: 'nowrap' }}
        />
      )
    }

    return (
      <Typography
        fontSize={getTitleFontSize(titleSize)}
        fontWeight={500}
        lineHeight="normal"
        whiteSpace="nowrap"
      >
        {title}
      </Typography>
    )
  }

  const handleChange = (event: any) => {
    const newValue = event.target.value
    setValue(newValue)
    setFieldError(required && !newValue)

    if (onFormValueChange) {
      onFormValueChange(name, newValue)
    }
  }
  const renderSelect = () => {
    const selectProps = {
      name,
      value: value || '',
      displayEmpty: true,
      label: outlinedLabel ? title : undefined,
      renderValue: (selected: any) => {
        if (!selected || selected.length === 0) {
          return <span style={{ color: '#999999' }}>{placeholder}</span>
        }
        return options.find((opt) => opt.value === selected)?.text
      },
      sx: {
        minHeight: '40px',
        backgroundColor: theme.palette.background.paper,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor:
            showError && (fieldError || error) && !value
              ? theme.palette.error.main
              : theme.palette.action.enabled,
          borderWidth: '1px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor:
            showError && (fieldError || error) && !value
              ? theme.palette.error.main
              : theme.palette.action.enabled,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor:
            (fieldError || error) && !value
              ? theme.palette.error.main
              : theme.palette.primary.main,
          borderWidth: '1px',
        },
        '&.Mui-disabled': {
          backgroundColor: alpha(theme.palette.action.disabled, 0.12),
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.action.disabled,
          },
        },
        '& .MuiInputBase-input': {
          paddingRight: '12px',
          paddingLeft: '12px',
          paddingTop: '8px',
          paddingBottom: '8px',
          letterSpacing: '0.15px',
          lineHeight: '1.5rem',
        },
        ...sx,
      },
      onChange: handleChange,
      MenuProps: {
        PaperProps: {
          sx: {
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
            '& .MuiMenuItem-root': {
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.24),
                },
              },
            },
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            ...menuStyle,
          },
        },
      },
      ...rest,
    }

    const renderOptions = () =>
      options.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={
            typeof optionDisabledChecker === 'function'
              ? optionDisabledChecker(option)
              : option?.disabled || !option?.value
          }
        >
          {option.text}
        </MenuItem>
      ))

    if (control) {
      return (
        <Controller
          name={name as keyof any}
          control={control}
          render={({
            field: { onChange: formOnChange, value: formValue, ...field },
            fieldState: { error: formFieldError },
          }) => (
            <>
              {outlinedLabel && title && (
                <InputLabel
                  sx={{
                    color:
                      showError && formFieldError && !formValue
                        ? theme.palette.error.main
                        : theme.palette.action.enabled,
                  }}
                  shrink
                  id={`${name}-label`}
                >
                  {title}
                </InputLabel>
              )}
              <MuiSelect
                {...field}
                {...selectProps}
                sx={{
                  ...selectProps.sx,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor:
                      showError && formFieldError && !formValue
                        ? theme.palette.error.main
                        : theme.palette.action.enabled,
                    borderWidth: '1px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor:
                      showError && formFieldError && !formValue
                        ? theme.palette.error.main
                        : theme.palette.action.enabled,
                  },
                }}
                value={formValue || ''}
                onChange={(event) => {
                  formOnChange(event)
                  handleChange(event)
                }}
              >
                {renderOptions()}
              </MuiSelect>
              {showError && formFieldError && !formValue && (
                <FormHelperText sx={{ color: theme.palette.error.main }}>
                  {formFieldError?.message || formFieldError?.root?.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      )
    }

    return (
      <>
        {outlinedLabel && title && (
          <InputLabel
            sx={{
              color:
                showError && (fieldError || error) && !value
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
              ...inputLabel,
            }}
            shrink
            id={`${name}-label`}
          >
            {title}
          </InputLabel>
        )}
        <MuiSelect {...selectProps}>{renderOptions()}</MuiSelect>
        {showError && (fieldError || error) && !value && (
          <FormHelperText sx={{ color: theme.palette.error.main }}>
            {`${name || 'This'} field is required`}
          </FormHelperText>
        )}
      </>
    )
  }

  return (
    <Box
      display="flex"
      alignItems="baseline"
      gap={direction === 'row' ? 1.5 : 1}
      flexDirection={direction}
      width="100%"
    >
      {!outlinedLabel && <Box sx={{}}>{renderTitle()}</Box>}

      <FormControl
        error={error || fieldError}
        required={required}
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        sx={{ width }}
      >
        {renderSelect()}
      </FormControl>
    </Box>
  )
}

export default Select
